import { LiveTvChannel, LiveTvChannelWithStream, LiveTvPlaybackType, LiveTvStream } from '../types/liveTv.types';

const CHANNELS_URL = '/live-tv/channels.json';
const STREAMS_URL = '/live-tv/streams.json';
const MANUAL_OVERRIDES_URL = '/live-tv/manual-overrides.json';

const TRUSTED_OFFICIAL_SOURCE_KEYWORDS = [
  'oficial',
  'official',
  'rtve',
  'dw',
  'france 24',
  'al jazeera',
  'euronews',
  'nhk',
  'nasa',
  'trt',
  'cgtn',
  'ndtv',
  'aaj tak',
  'tv azteca',
];

const PUBLIC_FREE_TO_AIR_SOURCE_KEYWORDS = ['iptv-org'];

const BLOCKED_SOURCE_KEYWORDS = ['fallback only'];

interface LiveTvManualOverrides {
  defaults?: Partial<LiveTvChannel>;
  channels?: Record<string, Partial<LiveTvChannel>>;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

async function fetchOptionalJson<T>(url: string): Promise<T | null> {
  try {
    return await fetchJson<T>(url);
  } catch (error) {
    console.warn(`[fastLiveTv] Optional file not loaded: ${url}`, error);
    return null;
  }
}

function normalizePlaybackType(value?: string): LiveTvPlaybackType {
  if (value === 'youtube' || value === 'official_embed') {
    return value;
  }

  return 'stream';
}

function getLiveTvStreamProxyBase() {
  return (import.meta.env.VITE_LIVE_TV_STREAM_PROXY_BASE || '').trim().replace(/\/+$/, '');
}

function getLiveTvStreamProxyMode() {
  return (import.meta.env.VITE_LIVE_TV_STREAM_PROXY_MODE || 'headers').toLowerCase();
}

function hasProxyHeaders(stream?: LiveTvStream) {
  return Boolean(stream?.http_referrer || stream?.user_agent);
}

export function shouldUseLiveTvStreamProxy(channel: LiveTvChannelWithStream): boolean {
  if (!channel.stream?.url) return false;
  if (!getLiveTvStreamProxyBase()) return false;

  const mode = getLiveTvStreamProxyMode();
  if (mode === 'off') return false;
  if (mode === 'all') return true;
  if (channel.proxyRequired) return true;

  return mode === 'headers' && hasProxyHeaders(channel.stream);
}

function getYouTubeEmbedParams() {
  const params = new URLSearchParams({
    autoplay: '1',
    playsinline: '1',
    rel: '0',
  });

  if (typeof window !== 'undefined' && window.location.origin) {
    params.set('origin', window.location.origin);
  }

  return params.toString();
}

export function getLiveTvYouTubeEmbedUrl(
  channel: Pick<LiveTvChannel, 'playbackType' | 'youtubeChannelId' | 'youtubeVideoId' | 'disableYouTubeFallback'>,
): string | null {
  if (channel.disableYouTubeFallback) {
    return null;
  }

  const params = getYouTubeEmbedParams();

  if (channel.playbackType === 'youtube' && channel.youtubeVideoId) {
    return `https://www.youtube.com/embed/${encodeURIComponent(channel.youtubeVideoId)}?${params}`;
  }

  if (channel.youtubeChannelId) {
    return `https://www.youtube.com/embed/live_stream?channel=${encodeURIComponent(channel.youtubeChannelId)}&${params}`;
  }

  return null;
}

function normalizeChannel(channel: LiveTvChannel): LiveTvChannel {
  return {
    ...channel,
    playbackType: normalizePlaybackType(channel.playbackType),
    enabled: typeof channel.enabled === 'boolean' ? channel.enabled : true,
    browserCompatible: typeof channel.browserCompatible === 'boolean' ? channel.browserCompatible : false,
    browserVerified: typeof channel.browserVerified === 'boolean' ? channel.browserVerified : false,
    forceEnable: typeof channel.forceEnable === 'boolean' ? channel.forceEnable : false,
    skipValidation: typeof channel.skipValidation === 'boolean' ? channel.skipValidation : false,
    isOfficial: typeof channel.isOfficial === 'boolean' ? channel.isOfficial : false,
    validationStatus: channel.validationStatus || 'unchecked',
    validationReason: channel.validationReason || '',
    lastCheckedAt: channel.lastCheckedAt || null,
  };
}

function applyManualOverrides(channels: LiveTvChannel[], overrides: LiveTvManualOverrides | null): LiveTvChannel[] {
  if (!overrides?.channels) {
    return channels;
  }

  return channels.map((channel) => {
    const channelOverride = overrides.channels?.[channel.id];

    if (!channelOverride) {
      return channel;
    }

    return {
      ...channel,
      ...channelOverride,
      id: channel.id,
      name: channelOverride.name ?? channel.name,
    };
  });
}

export function hasPlayableSource(channel: LiveTvChannelWithStream): boolean {
  return Boolean(
    isBrowserPlayableStreamUrl(channel.stream?.url) || getLiveTvYouTubeEmbedUrl(channel) || channel.embedUrl,
  );
}

export function isBrowserPlayableStreamUrl(url?: string): boolean {
  if (!url) return false;

  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    url.toLowerCase().startsWith('http://')
  ) {
    return false;
  }

  return true;
}

export function shouldAttemptLiveTvStream(channel: LiveTvChannelWithStream): boolean {
  if (!channel.stream?.url) return false;
  if (!isBrowserPlayableStreamUrl(channel.stream.url) && !shouldUseLiveTvStreamProxy(channel)) return false;

  const validationStatus = String(channel.validationStatus || '').toLowerCase();
  const validationReason = String(channel.validationReason || '').toLowerCase();

  return !(
    validationStatus.includes('vod') ||
    validationStatus.includes('ended') ||
    validationReason.includes('vod') ||
    validationReason.includes('has ended')
  );
}

export function getLiveTvStreamPlaybackUrl(channel: LiveTvChannelWithStream): string | null {
  if (!shouldAttemptLiveTvStream(channel)) return null;

  if (shouldUseLiveTvStreamProxy(channel)) {
    return `${getLiveTvStreamProxyBase()}/live-tv/proxy/${encodeURIComponent(channel.id)}`;
  }

  return channel.stream?.url || null;
}

export function isOfficialLiveTvChannel(channel: LiveTvChannelWithStream): boolean {
  if (!channel.isOfficial) return false;

  const source = (channel.source || '').toLowerCase();
  const hasTrustedSource = TRUSTED_OFFICIAL_SOURCE_KEYWORDS.some((keyword) => source.includes(keyword));
  const isBlockedSource = BLOCKED_SOURCE_KEYWORDS.some((keyword) => source.includes(keyword));

  if (!hasTrustedSource || isBlockedSource) {
    return false;
  }

  if (channel.playbackType === 'official_embed') {
    return Boolean(channel.embedUrl);
  }

  return hasPlayableSource(channel);
}

export function isPublicFreeToAirChannel(channel: LiveTvChannelWithStream): boolean {
  const source = (channel.source || '').toLowerCase();
  const isPublicFtaSource = PUBLIC_FREE_TO_AIR_SOURCE_KEYWORDS.some((keyword) => source.includes(keyword));
  const isBlockedSource = BLOCKED_SOURCE_KEYWORDS.some((keyword) => source.includes(keyword));

  return isPublicFtaSource && !isBlockedSource && hasPlayableSource(channel);
}

export function isTrustedLiveTvChannel(channel: LiveTvChannelWithStream): boolean {
  return isOfficialLiveTvChannel(channel) || isPublicFreeToAirChannel(channel);
}

export async function getLiveTvChannels(): Promise<LiveTvChannelWithStream[]> {
  const [rawChannels, streams, manualOverrides] = await Promise.all([
    fetchJson<LiveTvChannel[]>(CHANNELS_URL),
    fetchJson<LiveTvStream[]>(STREAMS_URL),
    fetchOptionalJson<LiveTvManualOverrides>(MANUAL_OVERRIDES_URL),
  ]);

  const streamMap = new Map<string, LiveTvStream>();

  for (const stream of streams) {
    if (!streamMap.has(stream.channel)) {
      streamMap.set(stream.channel, stream);
    }
  }

  const channelsWithOverrides = applyManualOverrides(rawChannels, manualOverrides);

  return channelsWithOverrides.map((channel) => {
    const normalized = normalizeChannel(channel);

    return {
      ...normalized,
      stream: streamMap.get(normalized.id),
    };
  });
}

export async function getLiveTvChannelById(channelId: string): Promise<LiveTvChannelWithStream | null> {
  const channels = await getLiveTvChannels();
  return channels.find((channel) => channel.id === channelId) ?? null;
}
